import {
  call,
  put,
  take,
  race,
  takeLatest,
  putResolve,
} from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { loginApi, refreshAuth, session, logoutApi } from './login.api';
import {
  userLoggedIn,
  userLoginFail,
} from '../../../common/actions/auth.actions';
import {
  storageRoles,
  storageAuthInformation,
  storageAuthRefreshToken,
  XSRFToken,
  tenantId,
} from '../../../common/constants/strings';
import {
  loginActions,
  logoutActions,
  session as sessionStart,
} from './login.actions';

import { TokenDto, LoginDto } from './login.dtos';
import { AuthDto, authInit } from '../../../common/dtos/auth';
import { parse } from 'path';
//import { Session } from 'inspector';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getCookie = (): string => {
  var cookie = document.cookie
    .split(';')
    .find((strCookie) => strCookie.indexOf(XSRFToken) >= 0);
  if (cookie != null) {
    return cookie.trim().substring(XSRFToken.length + 1);
  }
  return '';
};

function* authorize(creds: LoginDto) {
  try {
    const token: TokenDto = yield call(loginApi, creds);
    if (token !== undefined && token.isPasswordExpired) {
      yield put({
        type: getType(userLoginFail),
        payload: 'Password Update Required',
      });
      return token;
    } else if (token.authToken && token.authToken.length > 0) {
      // Cookie-based auth only. No token storage in JS.
      // Note: This flow is for Google Authenticator MFA (legacy), which still returns TokenDto.
      // Tokens are stored in HttpOnly cookies by the backend, not in sessionStorage.
      const auth: AuthDto = {
        status: 'authenticated',  // Login succeeded
        authToken: '',  // Empty - authentication via HttpOnly cookie only
        refreshToken: '',  // Empty - authentication via HttpOnly cookie only
        isAdmin: Boolean(token.roles.find((r: string) => r === 'admin')),
        isAuthenticated: true,
        isPasswordExpired: token.isPasswordExpired,
        roles: token.roles,
        fullName: token.fullName,
        employerId: token.employerId,
        contactId: token.employerId,
        employers: token.employers,
      };

      // Store only non-sensitive session metadata (roles, user info)
      // Do NOT store AuthToken or RefreshToken
      sessionStorage.setItem(storageRoles, JSON.stringify(token.roles));
      sessionStorage.setItem(storageAuthInformation, JSON.stringify({
        userId: token.userId,
        userName: token.userName,
        fullName: token.fullName,
        roles: token.roles,
        employers: token.employers,
        tenant: token.tenant,
        isPasswordExpired: token.isPasswordExpired,
        // Explicitly exclude authToken and refreshToken
      }));
      sessionStorage.setItem(storageAuthRefreshToken, token.refreshToken);  // Keep for refresh flow

      yield putResolve({ type: getType(userLoggedIn), payload: auth });

      const startSession: boolean = yield call(session);
      yield putResolve({ type: getType(sessionStart), payload: startSession });

      return token;
    } else if (token.mfaRequired !== undefined && token.mfaRequired) {
      let newToken: TokenDto = JSON.parse(JSON.stringify(token)) as TokenDto;
      newToken.userName = creds.username;
      newToken.userPassword = creds.password;
      yield put({ type: getType(loginActions.success), payload: newToken });
    } else {
      yield put({ type: getType(userLoginFail), payload: 'Login Failed' });
    }
  } catch (error) {
    yield put({ type: getType(userLoginFail), payload: 'Login Failed' });
  }

  return undefined;
}

function* loginAndRefreshToken(creds: ReturnType<typeof loginActions.request>) {
  const token: TokenDto = yield call(authorize, creds.payload);

  if (token !== undefined && token.isPasswordExpired) {
    sessionStorage.setItem(
      storageAuthInformation,
      JSON.stringify({
        ...JSON.parse(sessionStorage.getItem(storageAuthInformation) ?? 'null'),
        isPasswordExpired: token.isPasswordExpired,
      })
    );
    put({ type: getType(userLoginFail), payload: 'Password Update Required' });
  } else if (
    token !== undefined &&
    token.expiresIn !== undefined &&
    token.refreshToken !== undefined
  ) {
    while (true) {
      yield call(delay, token.expiresIn - 1000 * 60 * 3);

      let refreshToken = sessionStorage.getItem(storageAuthRefreshToken) ?? '';
      const newToken: TokenDto = yield call(refreshAuth, refreshToken);

      if (newToken.authToken && newToken.authToken.length > 0) {
        // Cookie-based auth only. No token storage in JS.
        // Tokens are stored in HttpOnly cookies by the backend, not in sessionStorage.
        const auth: AuthDto = {
          status: 'authenticated',  // Token refresh succeeded, still authenticated
          authToken: '',  // Empty - authentication via HttpOnly cookie only
          refreshToken: '',  // Empty - authentication via HttpOnly cookie only
          isAdmin: Boolean(newToken.roles.find((r: string) => r === 'admin')),
          isAuthenticated: true,
          isPasswordExpired: token.isPasswordExpired,
          roles: newToken.roles,
          fullName: newToken.fullName,
          employerId: token.employerId,
          contactId: token.employerId,
          employers: token.employers,
        };

        // Store only non-sensitive session metadata (roles, user info)
        // Do NOT store AuthToken or RefreshToken
        sessionStorage.setItem(storageRoles, JSON.stringify(newToken.roles));
        sessionStorage.setItem(
          storageAuthInformation,
          JSON.stringify({
            userId: newToken.userId,
            userName: newToken.userName,
            fullName: newToken.fullName,
            roles: newToken.roles,
            employers: newToken.employers,
            tenant: newToken.tenant,
            isPasswordExpired: newToken.isPasswordExpired,
            // Explicitly exclude authToken and refreshToken
          })
        );
        sessionStorage.setItem(storageAuthRefreshToken, token.refreshToken);  // Keep for refresh flow

        yield put({ type: getType(userLoggedIn), payload: auth });
      } else {
        put({ type: getType(userLoginFail), payload: 'Unauthorized' });
      }
    }
  }
}

function* handleLogout() {
  try {
    // Call backend logout endpoint to delete HttpOnly cookies
    yield call(logoutApi);
  } catch (err) {
    console.error('Logout API call failed:', err);
    // Continue with logout even if API call fails
  }

  // Set status to anonymous on logout
  const anonymousAuth: AuthDto = {
    ...authInit,
    status: 'anonymous',  // User logged out, now anonymous
  };
  yield put({ type: getType(userLoggedIn), payload: anonymousAuth });

  // Get tenant for redirect
  const tenant = localStorage.getItem(tenantId) ?? 'mha';

  // Clear session metadata
  sessionStorage.removeItem(storageRoles);
  sessionStorage.removeItem(storageAuthInformation);
  sessionStorage.removeItem(storageAuthRefreshToken);
  sessionStorage.clear();

  // Redirect to tenant-prefixed login page
  window.location.href = `/${tenant}/login`;
}

function* watchLoginRequest() {
  try {
    while (true) {
      const creds: ReturnType<typeof loginActions.request> = yield take(
        getType(loginActions.request)
      );
      yield race([
        take(getType(logoutActions.request)),
        call(loginAndRefreshToken, creds),
      ]);
    }
  } catch (err) {}
}

function* watchLogoutRequest() {
  yield takeLatest(getType(logoutActions.request), handleLogout);
}

export default [watchLoginRequest, watchLogoutRequest];
